import { useCallback } from 'react';

import { useGetShipmentById } from '@/features/shipments/api/getShipmentById';
import { useUpdateShipmentContractState } from '@/features/shipments/api/updateShipmentContractState';
import {
  CONTRACT_STATE,
  IModifiedShipment,
  IShipment,
  TContractState,
} from '@/features/shipments/models/shipment';

export interface ICleanupResult {
  success: boolean;
  hasShipment: boolean;
  needsUpdate: boolean;
  error?: string;
  message?: string;
}

interface IUpdateOperation {
  shipmentId: string;
  thingName?: string;
  contractState: TContractState;
}

// Constants
const CLEANUP_MESSAGES = {
  SHIPMENT_UPDATED: 'Shipment status updated',
  GO_AND_RETURN_UPDATED: 'Shipment and return statuses updated',
  ALL_FAILED: 'All contract state updates failed',
  PARTIAL_FAILURE: (success: number, total: number, failed: number) =>
    `${success}/${total} updates succeeded, ${failed} failed`,
  FETCH_FAILED: 'Failed to fetch shipment data',
  UPDATE_FAILED: 'Cleanup succeeded but shipment status update failed',
} as const;

/**
 * Extracts the Return shipment if exists
 */
const getReturnShipment = (shipment: IShipment): IModifiedShipment | null => {
  const isReturnShipment = !!shipment.originalShipmentId;

  if (isReturnShipment) {
    return null;
  }

  // For one-way shipments, returnShipment is null — returns null safely
  return (shipment.returnShipment as IModifiedShipment) || null;
};

/**
 * Builds the list of contract state update operations
 */
const buildUpdateOperations = (
  shipment: IShipment,
  returnShipment: IModifiedShipment | null
): { operations: IUpdateOperation[]; description: string } => {
  const operations: IUpdateOperation[] = [];

  // Operation 1: Update main shipment DEPLOYED → ARRIVED
  operations.push({
    shipmentId: shipment.id,
    thingName: shipment.box?.thingName,
    contractState: CONTRACT_STATE.ARRIVED,
  });

  let description: string = CLEANUP_MESSAGES.SHIPMENT_UPDATED;

  // Operation 2: Update Return TRANSFERRED → CANCELLED if exists
  const isReturnShipmentTransferred =
    returnShipment &&
    returnShipment.contractState === CONTRACT_STATE.TRANSFERRED;

  if (isReturnShipmentTransferred) {
    operations.push({
      shipmentId: returnShipment.id,
      thingName: returnShipment.box?.thingName,
      contractState: CONTRACT_STATE.CANCELLED,
    });
    description = CLEANUP_MESSAGES.GO_AND_RETURN_UPDATED;
  }

  return { operations, description };
};

/**
 * Executes update operations and analyzes results
 */
const executeAndAnalyzeUpdates = async (
  operations: IUpdateOperation[],
  updateContractState: (params: IUpdateOperation) => Promise<any>
): Promise<{ success: boolean; errorMessage?: string }> => {
  const updatePromises = operations.map((op) => updateContractState(op));
  const settledResults = await Promise.allSettled(updatePromises);

  const results = settledResults.map((result) => {
    if (result.status === 'fulfilled') {
      return result.value;
    }

    return { success: false, error: result.reason };
  });

  const failures = results.filter((result) => !result || !result.success);

  if (failures.length === 0) {
    return { success: true };
  }

  const successCount = results.length - failures.length;
  const errorMessage =
    failures.length === results.length
      ? CLEANUP_MESSAGES.ALL_FAILED
      : CLEANUP_MESSAGES.PARTIAL_FAILURE(
          successCount,
          results.length,
          failures.length
        );

  return { success: false, errorMessage };
};

/**
 * Hook to handle shipment status updates after a successful cleanup command.
 * Simplified logic:
 * - DEPLOYED shipment on box → ARRIVED
 * - If Return exists and is TRANSFERRED → CANCELLED
 */
export const useHandleCleanupSuccess = () => {
  const { fetchShipment } = useGetShipmentById();
  const { mutateAsync: updateContractState } = useUpdateShipmentContractState();

  /**
   * Handles shipment status updates after a successful cleanup
   * Updates DEPLOYED → ARRIVED and TRANSFERRED Return → CANCELLED
   * @returns ICleanupResult with detailed information about the process
   */
  const mutate = useCallback(
    async (currentShipmentId: string | undefined): Promise<ICleanupResult> => {
      if (!currentShipmentId) {
        return { success: true, hasShipment: false, needsUpdate: false };
      }

      try {
        const shipment = await fetchShipment(currentShipmentId);

        if (!shipment) {
          return {
            success: false,
            hasShipment: true,
            needsUpdate: false,
            error: CLEANUP_MESSAGES.FETCH_FAILED,
          };
        }

        if (shipment.contractState !== CONTRACT_STATE.DEPLOYED) {
          return {
            success: true,
            hasShipment: true,
            needsUpdate: false,
          };
        }

        const returnShipment = getReturnShipment(shipment);

        const { operations, description } = buildUpdateOperations(
          shipment,
          returnShipment
        );

        const { success, errorMessage } = await executeAndAnalyzeUpdates(
          operations,
          updateContractState
        );

        return {
          success,
          hasShipment: true,
          needsUpdate: true,
          error: success ? undefined : errorMessage,
          message: success ? description : undefined,
        };
      } catch (error) {
        console.error(
          '[handleCleanupSuccess] Failed to update shipment status:',
          error
        );

        return {
          success: false,
          hasShipment: true,
          needsUpdate: true,
          error: CLEANUP_MESSAGES.UPDATE_FAILED,
        };
      }
    },
    [fetchShipment, updateContractState]
  );

  return { mutate };
};
